"use client";

import React from "react";
import { Activity, ArrowLeftRight, ArrowRight, CircleCheck as CheckCircle2, Cpu, SlidersHorizontal } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const capabilitiesData = [{
  icon: Activity,
  title: "Quantitative & Qualitative Calibration Model Building",
  subtitle: "PLS, PCR, PCA & SIMCA Algorithms",
  description: "Robust chemometric models tailored for raw material identification, blend uniformity, and active ingredient assay in regulated environments.",
  deliverables: "Model validation documentation, outlier diagnostics, and precision verification.",
  tag: "Core Modeling"
}, {
  icon: SlidersHorizontal,
  title: "Routine Model Maintenance & Optimization",
  subtitle: "Continuous Lifecycle Monitoring",
  description: "Scheduled calibration drift analysis, slope and intercept adjustments, and expansion for new sample matrix variations and raw material suppliers.",
  deliverables: "Periodic health reviews, re-calibration reports, and change control support.",
  tag: "Lifecycle Support"
}, {
  icon: ArrowLeftRight,
  title: "Optical Method Transfer & Cross-Instrument Standardization",
  subtitle: "Seamless Instrument Equivalency",
  description: "Standardization protocols to transfer existing spectral libraries and quantitative methods between benchtop spectrometers and inline process probes.",
  deliverables: "Transfer protocol design, statistical equivalency tests, and risk assessments.",
  tag: "Standardization"
}, {
  icon: Cpu,
  title: "Advanced Multivariate Data Analysis",
  subtitle: "PAT & Real-Time Quality Control",
  description: "Multi-parameter process modeling combining inline spectral streams with critical quality attributes for batch release and endpoint determination.",
  deliverables: "Trajectory monitoring setups, alarm limit definitions, and automated data pipelines.",
  tag: "Advanced MVA"
}];
export default function Capabilities() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="capabilitiesgrid" data-nav="light" className="bg-muted/40 py-16 md:py-24 transition-colors">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 font-medium">
            Chemometric & Analytical Engineering
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Specialized Calibration and Multivariate Capabilities
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Application-led model development, method validation, and cross-platform transfer designed specifically for GMP pharmaceutical, chemical, and industrial manufacturing.
          </p>
        </div>

        {/* 4-Column Capabilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {capabilitiesData.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} whileHover={shouldReduceMotion ? undefined : {
            y: -6
          }} className="h-full flex">
                <Card className="flex-1 flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    <CardHeader className="space-y-3 pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {item.tag}
                        </span>
                      </div>
                      <CardTitle className="text-lg font-semibold text-card-foreground leading-snug">
                        {item.title}
                      </CardTitle>
                      <span className="text-xs font-medium text-primary block">
                        {item.subtitle}
                      </span>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-1">
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </CardDescription>

                      <div className="pt-3 border-t border-border/60">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <p className="text-xs text-foreground/80 leading-tight">
                            <span className="font-medium text-foreground">Includes: </span>
                            {item.deliverables}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                      Consult on this capability
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Bottom CTA Strip */}
        <div className="mt-12 md:mt-16 bg-card border border-border rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="text-base md:text-lg font-semibold text-foreground">
              Need model development or feasibility verification on your samples?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our spectroscopy specialists evaluate your spectral data and define optimal calibration strategies.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button variant="outline" size="default" asChild>
              <Link to="/FeasibilityStudiesPage">
                Feasibility Studies
              </Link>
            </Button>
            <Button variant="default" size="default" asChild>
              <Link to="/DiscussYourApplicationPage">
                Discuss Your Application
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}